import { useLayoutEffect, useRef } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useFieldContext } from "./formContext";
import type { TextareaFieldProps } from "./types";

export const TextareaField = ({
  label,
  isEditable,
  placeholder,
  maxLength,
  counterThreshold,
  minRows,
}: TextareaFieldProps) => {
  const { t } = useTranslation();
  const field = useFieldContext<string>();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    field.handleChange(event.target.value);
  };

  const value = field.state.value;
  const charactersLeft = maxLength - value.length;
  const error = field.state.meta.errors?.[0]?.message;
  const showsCounter = isEditable && charactersLeft <= counterThreshold;

  // Grows with the text instead of showing a fixed box with a resize grip.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (textarea === null) return;
    textarea.style.height = "auto";
    // scrollHeight excludes the border, which the box-sizing counts in the height.
    const borderHeight = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
  }, [value]);

  return (
    <div className="text-sm">
      <textarea
        ref={textareaRef}
        aria-label={label}
        className={clsx(
          "block max-h-80 w-full resize-none overflow-y-auto rounded-md border border-gray-200 px-2 py-1.5 text-sm leading-5 outline-none placeholder:text-gray-400 focus:ring-0",
          isEditable
            ? "bg-white text-gray-900 focus:border-blue-400"
            : "bg-gray-50 text-gray-700",
        )}
        rows={minRows}
        maxLength={maxLength}
        value={value}
        placeholder={isEditable ? placeholder : undefined}
        readOnly={!isEditable}
        onChange={handleChange}
        onBlur={field.handleBlur}
      />
      {(error !== undefined || showsCounter) && (
        <div className="mt-1 flex justify-between gap-2 text-xs">
          <span className="text-red-600">{error}</span>
          {showsCounter && (
            <span className="text-gray-500">
              {t("smartSurveyor.elementDetails.form.charactersLeft", {
                count: charactersLeft,
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
